import { tz } from "@date-fns/tz";
import { format, formatDistance, Locale } from "date-fns";

import { IntervalPrice, PricesData } from "api";

export type Source = "pse" | "rdn";

export function getCurrentSlot(timezone: string): string {
  const hhmm = format(new Date(), "HH:mm", { in: tz(timezone) });
  const [h, m] = hhmm.split(":").map(Number);
  const mins = Math.floor(m / 15) * 15;
  return `${String(h).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
}

// Today's delivery date in the plant's timezone — a viewer in another zone
// must not treat the plant's yesterday or tomorrow as the live day.
export function getCurrentDate(timezone: string): string {
  return format(new Date(), "yyyy-MM-dd", { in: tz(timezone) });
}

// Clock time of a source timestamp, in the plant's timezone, judged against
// the caller's reference clock so a string never mixes two of them. Exact rather than
// relative ("2 minutes ago"): a relative label reads wrong the moment the
// reference clock trails the timestamp — a just-finished fetch came out as
// "in less than 10 seconds" — and the hour is what the user wants to compare
// against the publication schedule anyway. Older than today keeps its date,
// since RDN for today was published during yesterday's session.
export function formatSourceTime(
  at: string | number | Date,
  now: Date,
  timezone: string,
  locale?: Locale,
): string {
  const options = { in: tz(timezone), locale };
  const sameDay =
    format(at, "yyyy-MM-dd", options) === format(now, "yyyy-MM-dd", options);
  return format(at, sameDay ? "HH:mm" : "d MMM HH:mm", options);
}

// How long ago a timestamp was, phrased against a ticking reference clock.
// The reference is pushed forward to the timestamp when it is the later of the
// two: a fetch that has just landed can be a fraction ahead of a clock that
// only ticks every few seconds, and date-fns would then describe something
// that already happened as being in the future ("za mniej niż 10 sekund").
export function formatTimeAgo(
  at: string | number | Date,
  now: Date,
  locale?: Locale,
): string {
  const then = new Date(at);
  const reference = then > now ? then : now;
  return formatDistance(then, reference, {
    addSuffix: true,
    includeSeconds: true,
    locale,
  });
}

// "5 minutes ago (10:16)" — the age answers "is this fresh?", the clock time
// answers "which publication am I looking at?".
export function formatTimestamp(
  at: string | number | Date,
  now: Date,
  timezone: string,
  locale?: Locale,
): string {
  return `${formatTimeAgo(at, now, locale)} (${formatSourceTime(at, now, timezone, locale)})`;
}

export function slotEnd(time: string): string {
  const [h, m] = time.split(":").map(Number);
  const total = h * 60 + m + 15;
  const eh = Math.floor(total / 60) % 24;
  const em = total % 60;
  return `${String(eh).padStart(2, "0")}:${String(em).padStart(2, "0")}`;
}

export function slotPeriod(time: string): string {
  return `${time} – ${slotEnd(time)}`;
}

export function latestEntry(
  data: PricesData | undefined,
  source: Source,
): IntervalPrice | null {
  if (!data) return null;
  for (let i = data.prices.length - 1; i >= 0; i--) {
    if (data.prices[i][source] !== null) return data.prices[i];
  }
  return null;
}

export function currentSlotEntry(
  data: PricesData | undefined,
  timezone: string,
): IntervalPrice | null {
  if (!data) return null;
  const slot = getCurrentSlot(timezone);
  return data.prices.find((p) => p.time === slot) ?? null;
}
